import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import TabNavigation from '../components/TabNavigation';
import PDFExportModal from '../components/PDFExportModal';
import FiveWhyAnalysis from '../components/FiveWhyAnalysis';
import FishboneAnalysis from '../components/FishboneAnalysis';
import StatutoryReportForm from '../components/StatutoryReportForm';
import { AuditTrailViewer } from '../components/AuditTrail';
import FileUpload from '../components/FileUpload';
import { useAlert } from '../context/AlertContext';
import { canApprove } from '../utils/roleUtils';

const HazardDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, confirm } = useAlert();
  const [hazard, setHazard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [showFiveWhy, setShowFiveWhy] = useState(false);
  const [showFishbone, setShowFishbone] = useState(false);
  const [showStatutoryForm, setShowStatutoryForm] = useState(null); // 'india' or 'singapore'
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'audit'
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoFiles, setPhotoFiles] = useState([]); // For new photo upload in edit mode

  useEffect(() => {
    fetchHazard();
  }, [id]);

  // Fetch photo URL when hazard is loaded and has a photo
  useEffect(() => {
    const fetchPhotoUrl = async () => {
      if (!hazard) return;

      // Check if hazard has a photo (either S3 or legacy base64)
      if (hazard.photo_s3_key || hazard.photo_base64) {
        setPhotoLoading(true);
        try {
          const response = await axios.get(`${API}/hazards/${id}/photo`);
          if (response.data.photo_url) {
            setPhotoUrl(response.data.photo_url);
          } else if (response.data.photo_base64) {
            // Legacy base64 fallback
            setPhotoUrl(response.data.photo_base64);
          }
        } catch (error) {
          console.error('Failed to fetch photo URL:', error);
          // If API fails but we have base64 in hazard object, use it as fallback
          if (hazard.photo_base64) {
            setPhotoUrl(hazard.photo_base64);
          }
        } finally {
          setPhotoLoading(false);
        }
      }
    };

    fetchPhotoUrl();
  }, [hazard, id]);

  const fetchHazard = async () => {
    try {
      const response = await axios.get(`${API}/hazards/${id}`);
      setHazard(response.data);
      setFormData(response.data);
    } catch (error) {
      console.error('Failed to fetch hazard:', error);
      showError('Failed to load incident details');
      navigate('/hazards');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoFilesChange = (files) => {
    setPhotoFiles(files);
  };

  const handlePhotoUploadError = (errors) => {
    errors.forEach(err => showError(err));
  };

  const handleUpdate = async () => {
    try {
       if (formData.recommended_actions && formData.recommended_actions.length > 0){
        formData.recommended_actions = formData.recommended_actions.split(';').map(action => { if (action.length > 0) return action.trim(); }).filter(Boolean);
      }else{
        formData.recommended_actions = [];
      }
      await axios.put(`${API}/hazards/${id}`, formData);

      // Upload new photo if one was selected
      if (photoFiles.length > 0) {
        const photoToUpload = photoFiles[0];
        if (photoToUpload.file && !photoToUpload.uploaded) {
          try {
            const photoFormData = new FormData();
            photoFormData.append('file', photoToUpload.file);

            await axios.post(`${API}/hazards/${id}/upload-photo`, photoFormData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });

            // Refresh hazard data to get new photo info
            await fetchHazard();
            // Clear the photo files state
            setPhotoFiles([]);
          } catch (photoErr) {
            console.error('Photo upload error:', photoErr.response?.data);
            showError('Incident updated but photo upload failed. You can try uploading again.');
          }
        }
      }

      setHazard(formData);
      setEditing(false);
      showSuccess('Incident updated successfully!');
    } catch (error) {
      console.error('Failed to update hazard:', error);
      showError('Failed to update incident');
    }
  };

  const handleEditingChange = formData => {
    console.log(formData);
    if(formData.recommended_actions.length > 0){
      const actions = formData.recommended_actions.join(';');
      setFormData(prev => ({ ...prev, recommended_actions: actions }));
    }
    const { name, value } = formData;
    setFormData(prev => ({ ...prev, [name]: value }));
    setEditing(true);
  };

  const handleMarkClosed = async () => {
    const confirmed = await confirm(
      'Close Incident',
      'Are you sure you want to mark this incident as closed?',
      { confirmLabel: 'Close Incident', destructive: true }
    );
    if (!confirmed) return;

    try {
      await axios.put(`${API}/hazards/${id}`, { status: 'Closed' });
      await fetchHazard();
      showSuccess('Incident has been successfully marked as closed!');
    } catch (error) {
      console.error('Failed to close incident:', error);
      if (error.response?.status === 401) {
        showError('Session expired. Please login again.');
        window.location.href = '/login';
      } else {
        showError('Failed to close incident. Please try again.');
      }
    }
  };

  const handleExportPDF = async () => {
    setShowPDFModal(true);
    setExportingPDF(true);
    
    try {
      // Wait for the animation to complete (simulate processing time)
      // Total animation time from modal: ~7 seconds
      await new Promise(resolve => setTimeout(resolve, 6500));
      
      const response = await axios.get(`${API}/export/pdf/${id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Saarv_Incident_Report_${id.substring(0, 8).toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Wait a bit more to show completion, then close modal
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowPDFModal(false);

      // Show success message
      setTimeout(() => {
        showSuccess('PDF report downloaded successfully!');
      }, 500);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      setShowPDFModal(false);
      if (error.response?.status === 401) {
        showError('Session expired. Please login again.');
        window.location.href = '/login';
      } else {
        showError('Failed to export PDF. Please try again.');
      }
    } finally {
      setExportingPDF(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'High':
        return 'bg-red-100 text-red-700 border-red-500';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-500';
      case 'Low':
        return 'bg-green-100 text-green-700 border-green-500';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-500';
    }
  };

  const handleStatutoryExport = (type) => {
    // Open the data collection form
    setShowStatutoryForm(type);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Closed':
        return 'bg-green-100 text-green-700 border-green-500';
      case 'Overdue':
        return 'bg-red-100 text-red-700 border-red-500';
      case 'In-Progress':
        return 'bg-blue-100 text-blue-700 border-blue-500';
      case 'Open':
        return 'bg-yellow-100 text-yellow-700 border-yellow-500';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#1B5C94]"></div>
        </div>
      </div>
      </div>
    );
  }

  if (!hazard) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-xl text-gray-600 mb-4">Incident not found</p>
            <button
              onClick={() => navigate('/hazards')}
              className="bg-[#1B5C94] text-white px-6 py-2 rounded-lg hover:bg-[#3AC0DB]"
            >
              Back to Incidents List
            </button>
          </div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6" data-testid="hazard-detail-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <button
              onClick={() => navigate('/hazards')}
              className="text-[#1B5C94] hover:text-[#3AC0DB] font-semibold mb-2 flex items-center"
            >
              ← Back to Incidents List
            </button>
            <h1 className="text-3xl font-bold text-gray-800">INCIDENT DETAILS</h1>
          </div>
          <div className="mt-4 md:mt-0 flex flex-wrap gap-2">
            <button
              onClick={handleExportPDF}
              disabled={exportingPDF}
              className="bg-gradient-to-r from-[#DAA520] to-[#C47723] text-white font-bold py-2 px-4 rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
              data-testid="export-pdf-button"
            >
              {exportingPDF ? 'Exporting...' : '📊 Export PDF'}
            </button>
            {(user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'hse_manager' || user?.role === 'HSE Manager') && !editing && (
              <button
                onClick={() => handleEditingChange(formData)}
                className="bg-[#1B5C94] text-white font-bold py-2 px-4 rounded-lg hover:bg-[#3AC0DB] transition-colors"
                data-testid="edit-button"
              >
                EDIT
              </button>
            )}
            {editing && (
              <>
                <button
                  onClick={handleUpdate}
                  className="bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                  data-testid="save-button"
                >
                  SAVE
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setFormData(hazard);
                    setPhotoFiles([]); // Clear any selected new photos
                  }}
                  className="bg-gray-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
                  data-testid="cancel-edit-button"
                >
                  CANCEL
                </button>
              </>
            )}
            {(user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'hse_manager' || user?.role === 'HSE Manager') && hazard?.status !== 'Closed' && !editing && (
              <button
                onClick={handleMarkClosed}
                className="bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                data-testid="mark-closed-button"
              >
                MARK AS CLOSED
              </button>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('details')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-[#1B5C94] text-[#1B5C94]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'audit'
                  ? 'border-[#1B5C94] text-[#1B5C94]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Audit History
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' ? (
          <>
        {/* RCA Tools & Statutory Reports Row */}
        {canApprove(user) && (
          <div className="bg-blue-50 rounded-xl p-4 flex flex-wrap gap-3">
            <h3 className="w-full text-sm font-bold text-gray-700 mb-2">🔍 Root Cause Analysis Tools</h3>
            
            <button
              onClick={() => setShowFiveWhy(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              5-Why Analysis
            </button>

            <button
              onClick={() => setShowFishbone(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              🐟 Fishbone Diagram
            </button>

            <div className="border-l-2 border-gray-300 mx-2"></div>

            <h3 className="w-full text-sm font-bold text-gray-700 mb-2 mt-2">📋 Statutory Reports</h3>

            <button
              onClick={() => handleStatutoryExport('india')}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-semibold"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              🇮🇳 India Form 18
            </button>

            <button
              onClick={() => handleStatutoryExport('singapore')}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              🇸🇬 Singapore MOM iReport
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-[#1B5C94] mb-4">Basic Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-gray-600">Title</label>
                  {editing ? (
                    <input
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      data-testid="edit-title-input"
                    />
                  ) : (
                    <p className="text-gray-800 font-semibold mt-1" data-testid="hazard-title">{hazard.title}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Incident Type</label>
                    {editing ? (
                      <select
                        name="hazard_type"
                        value={formData.hazard_type}
                        onChange={handleInputChange}
                        className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                        data-testid="edit-type-select"
                      >
                        <option value="Near-Miss">Near-Miss</option>
                        <option value="First Aid">First Aid</option>
                        <option value="Medical Treatment">Medical Treatment</option>
                        <option value="Restricted Work">Restricted Work</option>
                        <option value="Lost Time Injury">Lost Time Injury</option>
                        <option value="Property Damage">Property Damage</option>
                        <option value="Environmental">Environmental</option>
                        <option value="Unsafe Condition">Unsafe Condition</option>
                        <option value="Unsafe Act">Unsafe Act</option>
                        <option value="Equipment Failure">Equipment Failure</option>
                        <option value="Other">Other</option>
                      </select>
                    ) : (
                      <p className="text-gray-800 mt-1" data-testid="hazard-type">{hazard.hazard_type}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Auto Classification</label>
                    <p className="text-gray-800 mt-1" data-testid="auto-classification">{hazard?.auto_classification || 'N/A'}</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-600">Description</label>
                  {editing ? (
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      data-testid="edit-description-input"
                    />
                  ) : (
                    <p className="text-gray-800 mt-1" data-testid="hazard-description">{hazard.description}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Date of Incident</label>
                    {editing ? (
                      <input
                        name="incident_date"
                        type="date"
                        value={formData.incident_date || ''}
                        onChange={handleInputChange}
                        className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    ) : (
                      <p className="text-gray-800 mt-1">
                        {hazard.incident_date 
                          ? new Date(hazard.incident_date).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })
                          : 'Not specified'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Location</label>
                    {editing ? (
                      <input
                        name="location"
                        value={formData.location || ''}
                        onChange={handleInputChange}
                        className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    ) : (
                      <p className="text-gray-800 mt-1">{hazard.location || 'N/A'}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Equipment Involved</label>
                    {editing ? (
                      <input
                        name="equipment_involved"
                        value={formData.equipment_involved || ''}
                        onChange={handleInputChange}
                        className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    ) : (
                      <p className="text-gray-800 mt-1">{hazard.equipment_involved || 'N/A'}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-600">Immediate Action Taken</label>
                  {editing ? (
                    <textarea
                      name="immediate_action"
                      value={formData.immediate_action || ''}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                    />
                  ) : (
                    <p className="text-gray-800 mt-1">{hazard.immediate_action || 'N/A'}</p>
                  )}
                </div>

                {/* Lost Time Injury Flag */}
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                  <label className="flex items-center cursor-pointer">
                    {editing ? (
                      <input
                        type="checkbox"
                        name="is_lti"
                        checked={formData.is_lti || false}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_lti: e.target.checked }))}
                        className="w-5 h-5 text-[#1B5C94] border-gray-300 rounded focus:ring-[#1B5C94] focus:ring-2"
                      />
                    ) : (
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded ${hazard.is_lti ? 'bg-red-500' : 'bg-gray-300'}`}>
                        {hazard.is_lti && (
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </span>
                    )}
                    <span className="ml-3">
                      <span className="block text-sm font-bold text-gray-800">Lost Time Injury (LTI)</span>
                      <span className="block text-xs text-gray-600 mt-1">
                        {hazard.is_lti ? 'This incident resulted in time away from work' : 'Not classified as Lost Time Injury'}
                      </span>
                    </span>
                  </label>
                  
                  {/* Medical Leave Days - Show if LTI */}
                  {(hazard.is_lti || formData.is_lti) && (
                    <div className="mt-3 pt-3 border-t border-yellow-300">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Medical Leave Days <span className="text-red-500">*</span>
                      </label>
                      {editing ? (
                        <input
                          type="number"
                          name="medical_leave_days"
                          step="0.5"
                          min="0"
                          value={formData.medical_leave_days || ''}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                          placeholder="Enter number of days"
                        />
                      ) : (
                        <p className="text-gray-800 font-semibold">
                          {hazard.medical_leave_days ? `${hazard.medical_leave_days} days` : 'Not specified'}
                        </p>
                      )}
                      <p className="text-xs text-gray-600 mt-1">Required for Singapore MOM iReport compliance</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Photo */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-[#1B5C94] mb-4">Photo</h2>

              {/* Show existing photo */}
              {photoLoading ? (
                <div className="flex items-center justify-center h-48 bg-gray-100 rounded-lg">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#1B5C94]"></div>
                </div>
              ) : photoUrl ? (
                <img src={photoUrl} alt="Incident" className="w-full rounded-lg shadow-md mb-4" />
              ) : !editing ? (
                <p className="text-gray-500 text-sm">No photo attached</p>
              ) : null}

              {/* File upload in edit mode */}
              {editing && (
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-gray-600 mb-2">
                    {photoUrl ? 'Replace Photo' : 'Upload Photo'}
                  </label>
                  <FileUpload
                    files={photoFiles}
                    onFilesChange={handlePhotoFilesChange}
                    multiple={false}
                    accept="image/*"
                    maxSizeMB={10}
                    label={photoUrl ? 'Click to replace image' : 'Click to upload image'}
                    showSizeLimit={true}
                    onError={handlePhotoUploadError}
                  />
                  {photoUrl && photoFiles.length === 0 && (
                    <p className="text-xs text-gray-500 mt-2">Current photo will be kept unless you upload a new one</p>
                  )}
                </div>
              )}
            </div>

            {/* Management Actions */}
            {(user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'hse_manager' || user?.role === 'HSE Manager') && (
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-xl font-bold text-[#1B5C94] mb-4">Management Actions</h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Root Cause</label>
                    {editing ? (
                      <select
                        name="root_cause"
                        value={formData.root_cause || ''}
                        onChange={handleInputChange}
                        className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      >
                        <option value="">Select root cause</option>
                        <option>Unsafe act</option>
                        <option>Unsafe condition</option>
                        <option>Equipment failure</option>
                        <option>Lack of training</option>
                        <option>Poor maintenance</option>
                        <option>Other</option>
                      </select>
                    ) : (
                      <p className="text-gray-800 mt-1">{hazard.root_cause || 'Not specified'}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Corrective Action</label>
                    {editing ? (
                      <textarea
                        name="corrective_action"
                        value={formData.corrective_action || ''}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    ) : (
                      <p className="text-gray-800 mt-1">{hazard.corrective_action || 'Not specified'}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-600">Recommended Future Actions</label>
                    {editing ? (
                      <div>
                        <textarea
                          name="recommended_actions"
                          value={formData.recommended_actions || ''}
                          onChange={handleInputChange}
                          rows="3"
                          className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                          placeholder="Enter preventive measures separated by semicolons"
                        />
                        <p className="text-xs text-gray-500 mt-1">Separate multiple actions with semicolons (;)</p>
                      </div>
                    ) : (
                      <div className="mt-1">
                        {hazard.recommended_actions && hazard.recommended_actions.length > 0 ? (
                          <ul className="list-disc list-inside space-y-1">
                            {hazard.recommended_actions.map((action, idx) => (
                              <li key={idx} className="text-gray-800 text-sm">{action}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-gray-800">Not specified</p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Responsible Person</label>
                      {editing ? (
                        <input
                          name="responsible_person"
                          value={formData.responsible_person || ''}
                          onChange={handleInputChange}
                          className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                        />
                      ) : (
                        <p className="text-gray-800 mt-1">{hazard.responsible_person || 'Not assigned'}</p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm font-semibold text-gray-600">Target Date</label>
                      {editing ? (
                        <input
                          name="target_date"
                          type="date"
                          value={formData.target_date || ''}
                          onChange={handleInputChange}
                          className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                        />
                      ) : (
                        <p className="text-gray-800 mt-1">{hazard.target_date || 'Not set'}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Status & Metadata */}
          <div className="space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Status</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-gray-600">Current Status</label>
                  {editing ? (
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      data-testid="edit-status-select"
                    >
                      <option>Open</option>
                      <option>In-Progress</option>
                      <option>Closed</option>
                      <option>Overdue</option>
                    </select>
                  ) : (
                    <div className="mt-2">
                      <span className={`inline-block px-4 py-2 rounded-lg text-sm font-bold border-2 ${getStatusColor(hazard.status)}`} data-testid="hazard-status">
                        {hazard.status}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-600">Severity</label>
                  {editing ? (
                    <select
                      name="severity"
                      value={formData.severity}
                      onChange={handleInputChange}
                      className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      data-testid="edit-severity-select"
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  ) : (
                    <div className="mt-2">
                      <span className={`inline-block px-4 py-2 rounded-lg text-sm font-bold border-2 ${getSeverityColor(hazard.severity)}`} data-testid="hazard-severity">
                        {hazard.severity}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Assignment Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Assignment</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-gray-600">Assigned To</label>
                  {editing ? (
                    <input
                      name="assignee"
                      value={formData.assignee}
                      onChange={handleInputChange}
                      className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                    />
                  ) : (
                    <p className="text-gray-800 font-semibold mt-1">{hazard.assignee}</p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-600">Due Date</label>
                  {editing ? (
                    <input
                      name="due_date"
                      type="date"
                      value={formData.due_date || ''}
                      onChange={handleInputChange}
                      className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                    />
                  ) : (
                    <p className="text-gray-800 mt-1">{hazard.due_date || 'Not set'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Metadata Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Details</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-gray-600 font-semibold">Incident ID:</span>
                  <p className="text-gray-800 mt-1 text-xs font-mono">{hazard.id}</p>
                </div>
                <div>
                  <span className="text-gray-600 font-semibold">Reported By:</span>
                  <p className="text-gray-800 mt-1">{hazard.reported_by_name || hazard.reported_by || 'Unknown'}</p>
                </div>
                <div>
                  <span className="text-gray-600 font-semibold">Date Reported:</span>
                  <p className="text-gray-800 mt-1">
                    {hazard.date_reported ? new Date(hazard.date_reported).toLocaleString() : 'Not specified'}
                  </p>
                </div>
                {hazard.closed_date && (
                  <div>
                    <span className="text-gray-600 font-semibold">Closed Date:</span>
                    <p className="text-gray-800 mt-1">
                      {new Date(hazard.closed_date).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
          </>
        ) : (
          /* Audit History Tab */
          <div className="bg-white rounded-xl shadow-md p-6">
            <AuditTrailViewer
              entityType="hazard"
              entityId={id}
              title="Incident Audit History"
            />
          </div>
        )}
      </div>

      {/* PDF Export Progress Modal */}
      <PDFExportModal 
        isOpen={showPDFModal}
        onClose={() => setShowPDFModal(false)}
        hazardId={id}
      />

      {/* RCA Modals */}
      {showFiveWhy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="max-w-4xl w-full my-8">
              <FiveWhyAnalysis hazardId={id} onClose={() => setShowFiveWhy(false)} />
            </div>
          </div>
        </div>
      )}

      {showFishbone && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-4">
            <div className="max-w-6xl w-full my-8">
              <FishboneAnalysis hazardId={id} onClose={() => setShowFishbone(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Statutory Report Form */}
      {showStatutoryForm && hazard && (
        <StatutoryReportForm 
          hazard={hazard}
          reportType={showStatutoryForm}
          onClose={() => setShowStatutoryForm(null)}
        />
      )}
    </div>
      </div>
  );
};

export default HazardDetail;